import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** English mirror of the Thai NAATI vs court-registered translator guide. */
const TITLE = "NAATI vs Court-Registered Thai Translators — Which One Does Your Authority Accept?";
const DESC =
  "When Australian authorities require a NAATI-certified translation, when Thai court or Lawyers Council registration is the right choice, and how to avoid paying for the wrong one.";
const PATH = "/blog/naati-vs-court-registered-translator";
const URL = `/en${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "What is a NAATI-certified translation?",
    answer:
      "NAATI is Australia's national certification body for translators and interpreters. A NAATI-certified translation is produced by a practitioner holding current NAATI certification for the relevant language and direction, and it carries the practitioner's stamp and statement. Australian visa, licensing, and academic bodies are the main audience for it.",
  },
  {
    question: "When is a court-registered or Lawyers Council translator the right choice?",
    answer:
      "When the document will be used inside Thailand — in court proceedings, with Thai government offices, or as part of a Thai legalisation chain — the accepted practitioner is usually one recognised by the Thai authority receiving the file, such as a translator whose work the Department of Consular Affairs accepts.",
  },
  {
    question: "Can one translation satisfy both Australia and Thailand?",
    answer:
      "Sometimes, but you should not assume it. The two systems answer to different authorities. Where a file will be used in both places, the safe approach is to confirm each authority's requirement separately and, if they differ, produce two sets rather than argue the point at the counter.",
  },
  {
    question: "Does a NAATI translation need Thai government certification?",
    answer:
      "It depends on the receiving body. Many Australian authorities accept the NAATI translation on its own, while others also want the Thai original certified by the Department of Consular Affairs. Ask before the translation is produced, because certification of the original normally comes first.",
  },
  {
    question: "How do I verify a translator's credentials?",
    answer:
      "For NAATI, check the practitioner number and the certification direction and expiry on the certification record. For Thai practitioners, confirm the registration the receiving authority actually recognises, since registration with one body does not automatically satisfy another.",
  },
  {
    question: "What makes a translation fail even when the translator is qualified?",
    answer:
      "Omitting text inside seals and signatures, transliterating names differently from the passport, missing pages, ambiguous calendar years, and no statement or stamp identifying the translator. These are formatting failures rather than language failures, and they are the most common cause of returns.",
  },
  {
    question: "Which should I arrange first: translation or certification?",
    answer:
      "As a rule, certify the Thai original first, then translate, then certify the translation if required. Translating first means the certification applied afterwards will not appear in the translated set, which forces the work to be redone.",
  },
];

export const Route = createFileRoute("/en/blog/naati-vs-court-registered-translator")({
  component: EnNaatiVsCourtTranslator,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "en" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("naati-vs-court-registered-translator") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("naati-vs-court-registered-translator", "en") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("naati-vs-court-registered-translator") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "naati certified translation thailand, naati thai translator bangkok, court registered translator thailand, certified thai english translation, sworn translator thailand",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "en"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Home", url: "/en" },
              { name: "Blog", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("naati-vs-court-registered-translator"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Translation",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const CHOOSE: Array<{ scenario: string; answer: string }> = [
  {
    scenario: "Australian visa, partner or skilled migration file",
    answer: "NAATI-certified translation, and confirm whether the Thai original also needs consular certification.",
  },
  {
    scenario: "Enrolment or credential assessment in Australia",
    answer: "NAATI-certified translation of transcripts and certificates, produced from certified originals.",
  },
  {
    scenario: "Thai court proceedings or filings with Thai authorities",
    answer: "A translator the receiving Thai authority recognises; NAATI status carries no weight there.",
  },
  {
    scenario: "Thai document going to a non-Australian destination",
    answer: "Follow the destination authority's rule; often certification by the Department of Consular Affairs plus an embassy step.",
  },
  {
    scenario: "Foreign document to be used in Thailand",
    answer: "Translation into Thai plus the certification chain the Thai receiving office accepts.",
  },
];

const CHECKS: string[] = [
  "Certification direction covers the language pair you actually need",
  "Certification is current on the date the translation is produced",
  "Every page, seal, stamp, and handwritten note is rendered",
  "Name transliteration matches the passport exactly across the whole file",
  "The translator's statement, stamp, and contact details appear on the translation",
  "The certified original is in hand before translation begins",
];

function EnNaatiVsCourtTranslator() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/en" className="text-muted-foreground hover:text-foreground">
          Home
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Blog
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="naati-vs-court-registered-translator" locale="en" />
      <p className="answer-summary speakable-summary">
        NAATI certification and Thai court or Lawyers Council registration are not competing quality
        grades — they are recognition by different authorities. Australian bodies generally want
        NAATI. Thai courts and offices want a practitioner they recognise. The practical question is
        never "which translator is better", it is "which authority receives this file", and the
        answer to that question should be settled before any translation is ordered.
      </p>

      <h2>1. Two systems, two audiences</h2>
      <p>
        NAATI exists so that Australian institutions can rely on a single national credential without
        assessing each translator themselves. Thai recognition works the same way for Thai
        institutions. Because each system serves its own jurisdiction, a credential accepted in one
        place has no automatic standing in the other. That is why a technically excellent translation
        can still be refused: the refusal is about recognition, not quality.
      </p>

      <h2>2. Choosing by scenario</h2>
      <ul>
        {CHOOSE.map((c) => (
          <li key={c.scenario}>
            <strong>{c.scenario}:</strong> {c.answer}
          </li>
        ))}
      </ul>

      <h2>3. Order of work</h2>
      <p>
        In almost every cross-border file the sequence is the same: obtain a current original,
        notarise signatures where the document is private, certify the original with the Thai
        authority when required, translate, certify the translation if the destination asks for it,
        and only then submit. Reversing translation and certification is the single most common
        reason a file comes back, because the certification applied later never appears in the
        translated set.
      </p>

      <h2>4. Pre-flight checks before you pay</h2>
      <ul>
        {CHECKS.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>

      <h2>5. When both sets are worth producing</h2>
      <p>
        If the same documents will support an Australian application and a Thai process — for
        example a marriage registered in Thailand supporting a partner visa — producing two
        translation sets is usually cheaper than a rejection. The originals and certifications are
        shared; only the translation layer differs. Deciding this at the planning stage avoids
        reordering certified originals under time pressure.
      </p>

      <h2>6. Frequently asked questions</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>7. References and related services</h2>
      <p className="text-sm">
        References:{" "}
        <a href="https://www.naati.com.au/" target="_blank" rel="noreferrer">
          NAATI
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Department of Consular Affairs, Ministry of Foreign Affairs (Thailand)
        </a>{" "}
        ·{" "}
        <a href="https://www.lawyerscouncil.or.th/" target="_blank" rel="noreferrer">
          Lawyers Council of Thailand
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/en/naati" className="text-primary underline">
            NAATI-certified translation service
          </Link>
        </li>
        <li>
          <Link to="/en/sworn" className="text-primary underline">
            Sworn and certified translation service
          </Link>
        </li>
        <li>
          <Link to="/en/blog/apostille-vs-legalization-by-country" className="text-primary underline">
            Apostille vs legalisation, country by country
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Content reviewed on {PUBLISHED}. This guide is general information for planning purposes and
        is not a determination made on behalf of any authority. Please confirm current requirements
        with the receiving authority before you proceed.
      </p>
    </article>
  );
}
